import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from './ui/sheet';
import { Button } from './ui/button';
import {
  Mail, Phone, Building2, User, ShieldCheck, Users as UsersIcon,
  Activity, KeyRound, PowerOff, UserCheck, Trash2, Edit, Clock,
  CalendarDays, CheckCircle2, XCircle, ChevronRight,
} from 'lucide-react';
import { format, formatDistanceToNow, parseISO, isToday, isYesterday } from 'date-fns';
import { toast } from 'sonner';
import { Switch } from './ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// Small helpers (kept local so the drawer is self-contained)
// -----------------------------------------------------------------------------
const AVATAR_PALETTE = [
  'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-orange-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-rose-500', 'bg-indigo-500',
  'bg-amber-500', 'bg-teal-500', 'bg-fuchsia-500', 'bg-lime-600',
];
const colorForId = (id = '') => AVATAR_PALETTE[
  Math.abs([...id].reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_PALETTE.length
];
const initialsOf = (name = '') => name
  .split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const humanTime = (iso) => {
  if (!iso) return 'Never';
  try {
    const d = parseISO(iso);
    if (isToday(d)) return `Today at ${format(d, 'hh:mm a')}`;
    if (isYesterday(d)) return `Yesterday at ${format(d, 'hh:mm a')}`;
    return `${formatDistanceToNow(d)} ago`;
  } catch { return '—'; }
};

const humanAction = (action = '') => action
  .replaceAll('_', ' ')
  .replace(/\b\w/g, c => c.toUpperCase());

// -----------------------------------------------------------------------------
// Section shell — consistent header + spacing across tabs
// -----------------------------------------------------------------------------
const Section = ({ title, Icon, children, testId }) => (
  <section className="space-y-3" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-800">
      <Icon size={16} className="text-blue-600" />
      <h3 className="text-sm font-semibold uppercase tracking-wider">{title}</h3>
    </div>
    <div className="pl-1">{children}</div>
  </section>
);

const InfoRow = ({ label, value, Icon }) => (
  <div className="flex items-start gap-3 py-2">
    {Icon && <Icon size={14} className="text-slate-400 mt-0.5 shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-medium uppercase text-slate-400 tracking-wide">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5 break-words">{value || <span className="italic text-slate-400">—</span>}</p>
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// Main drawer
// -----------------------------------------------------------------------------
const UserDetailDrawer = ({
  open,
  onOpenChange,
  user,             // Row data (has id, full_name, email, rbac_role_id, ...)
  role,             // Resolved rbac_role for `user`
  department,       // Resolved department object (or null)
  manager,          // Resolved reports_to user (or null)
  onEdit,           // (user) => void  — trigger Edit modal
  onDelete,         // (user_id) => void
  onToggleStatus,   // (user) => void
  onResetPassword,  // (user) => Promise
  isSelf = false,
  RoleBadge,        // component reused from the parent for visual consistency
  onToggleFullView, // (roleId, enabled) => void — Social Media full_view toggle (whole role)
  fullViewBusy = false,
}) => {
  const { token } = useAuth();
  const [tab, setTab] = useState('personal'); // personal | permissions | activity
  const [permissions, setPermissions] = useState(null);
  const [permsLoading, setPermsLoading] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Reset local tab state whenever a different user is opened.
  useEffect(() => {
    if (open) {
      setTab('personal');
      setPermissions(null);
      setActivity([]);
    }
  }, [open, user?.id]);

  // Lazy-load permissions when the Permissions tab is first opened for this user.
  useEffect(() => {
    if (!open || !user?.id || tab !== 'permissions' || permissions) return;
    setPermsLoading(true);
    axios.get(`${API}/rbac/users/${user.id}/permissions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setPermissions(r.data))
      .catch(() => setPermissions({ error: true }))
      .finally(() => setPermsLoading(false));
  }, [open, user?.id, tab, permissions, token]);

  // Lazy-load recent activity when the Activity tab is opened.
  useEffect(() => {
    if (!open || !user?.id || tab !== 'activity' || activity.length > 0) return;
    setActivityLoading(true);
    axios.get(`${API}/audit-logs`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { target_user_id: user.id, limit: 15 },
    })
      .then(r => setActivity(r.data?.logs || []))
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, [open, user?.id, tab, activity.length, token]);

  if (!user) return null;

  const status = user.status || 'Active';
  const active = status === 'Active';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto p-0 flex flex-col"
        data-testid="user-detail-drawer"
      >
        {/* Hero header — gradient banner + avatar block */}
        <div className="bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 px-6 pt-8 pb-16 relative">
          <SheetHeader className="hidden">
            <SheetTitle>User Detail</SheetTitle>
          </SheetHeader>
          <div className="flex items-start justify-between text-white">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/70">User Profile</p>
              <h2 className="text-lg font-semibold mt-1" data-testid="drawer-user-name-hero">{user.full_name}</h2>
              <p className="text-xs text-white/80">{role?.display_name || 'User'}</p>
            </div>
          </div>
        </div>

        {/* Overlapping avatar + quick actions bar */}
        <div className="px-6 -mt-10">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-5">
            <div className="flex items-start gap-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white text-xl font-bold overflow-hidden shrink-0 ${colorForId(user.id)}`}>
                {user.profile_picture
                  ? <img src={user.profile_picture} alt={user.full_name} className="w-full h-full object-cover" />
                  : initialsOf(user.full_name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-bold text-slate-900 truncate" data-testid="drawer-user-name">{user.full_name}</h3>
                  {isSelf && <span className="text-[10px] font-semibold px-2 py-0.5 bg-blue-50 text-blue-600 rounded">You</span>}
                </div>
                <p className="text-sm text-slate-500 truncate">{user.email}</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {RoleBadge && role && <RoleBadge role={role} />}
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${active ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'}`}
                    data-testid="drawer-status-badge"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    {status}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick action row */}
            <div className="mt-4 grid grid-cols-4 gap-2">
              <button
                className="flex flex-col items-center justify-center gap-1 py-2 rounded-lg hover:bg-blue-50 text-blue-600 transition"
                onClick={() => onEdit?.(user)}
                data-testid="drawer-action-edit"
                title="Edit user"
              >
                <Edit size={16} />
                <span className="text-[11px] font-medium">Edit</span>
              </button>
              <button
                className="flex flex-col items-center justify-center gap-1 py-2 rounded-lg hover:bg-amber-50 text-amber-600 transition disabled:opacity-50"
                onClick={() => onResetPassword?.(user)}
                data-testid="drawer-action-reset-pw"
                title="Send password-reset email"
              >
                <KeyRound size={16} />
                <span className="text-[11px] font-medium">Reset PW</span>
              </button>
              {active ? (
                <button
                  className="flex flex-col items-center justify-center gap-1 py-2 rounded-lg hover:bg-slate-100 text-slate-600 transition disabled:opacity-50"
                  onClick={() => onToggleStatus?.(user)}
                  disabled={isSelf}
                  data-testid="drawer-action-deactivate"
                  title="Deactivate user"
                >
                  <PowerOff size={16} />
                  <span className="text-[11px] font-medium">Deactivate</span>
                </button>
              ) : (
                <button
                  className="flex flex-col items-center justify-center gap-1 py-2 rounded-lg hover:bg-emerald-50 text-emerald-600 transition"
                  onClick={() => onToggleStatus?.(user)}
                  data-testid="drawer-action-reactivate"
                  title="Reactivate user"
                >
                  <UserCheck size={16} />
                  <span className="text-[11px] font-medium">Activate</span>
                </button>
              )}
              <button
                className="flex flex-col items-center justify-center gap-1 py-2 rounded-lg hover:bg-red-50 text-red-600 transition disabled:opacity-50"
                onClick={() => onDelete?.(user.id)}
                disabled={isSelf}
                data-testid="drawer-action-delete"
                title="Delete user"
              >
                <Trash2 size={16} />
                <span className="text-[11px] font-medium">Delete</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 mt-5 border-b border-slate-200 flex items-center gap-1">
          {[
            { key: 'personal',    label: 'Personal',    Icon: User },
            { key: 'permissions', label: 'Permissions', Icon: ShieldCheck },
            { key: 'activity',    label: 'Activity',    Icon: Activity },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`relative flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors ${tab === t.key ? 'text-blue-600' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid={`drawer-tab-${t.key}`}
            >
              <t.Icon size={14} />
              {t.label}
              {tab === t.key && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-blue-600 rounded-full" />}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex-1 overflow-y-auto space-y-6">
          {tab === 'personal' && (
            <>
              <Section title="Contact" Icon={Mail} testId="drawer-section-contact">
                <InfoRow Icon={Mail}  label="Email"          value={user.email} />
                <InfoRow Icon={Phone} label="Mobile"         value={user.mobile_number ? `${user.mobile_country_code || ''} ${user.mobile_number}`.trim() : ''} />
                <InfoRow Icon={Building2} label="Department" value={department?.name} />
              </Section>

              <Section title="Reporting Hierarchy" Icon={UsersIcon} testId="drawer-section-manager">
                {manager ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold overflow-hidden ${colorForId(manager.id)}`}>
                      {manager.profile_picture
                        ? <img src={manager.profile_picture} alt={manager.full_name} className="w-full h-full object-cover" />
                        : initialsOf(manager.full_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{manager.full_name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {manager.role_display || 'Manager'} · {manager.email}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-slate-400" />
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No manager assigned.</p>
                )}
              </Section>

              <Section title="Timeline" Icon={CalendarDays} testId="drawer-section-timeline">
                <InfoRow Icon={CalendarDays} label="Joined" value={user.created_at ? format(parseISO(user.created_at), 'MMMM d, yyyy') : ''} />
                <InfoRow Icon={Clock} label="Last Updated" value={user.updated_at ? format(parseISO(user.updated_at), 'MMMM d, yyyy · h:mm a') : ''} />
                <InfoRow Icon={Clock} label="Last Login" value={humanTime(user.last_login_at)} />
              </Section>
            </>
          )}

          {tab === 'permissions' && (
            <div className="space-y-5" data-testid="drawer-permissions-body">
              {permsLoading && <p className="text-sm text-slate-500">Loading permissions…</p>}
              {!permsLoading && permissions?.error && (
                <p className="text-sm text-red-500">Could not load permissions.</p>
              )}
              {!permsLoading && permissions && !permissions.error && (
                <>
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                    <p className="text-xs uppercase text-blue-500 font-semibold tracking-wider">Effective Role</p>
                    <p className="text-lg font-bold text-blue-900 mt-1">
                      {permissions.role_display_name || permissions.role_name || '—'}
                    </p>
                    <p className="text-xs text-blue-700/70 mt-0.5">
                      Legacy level: {permissions.legacy_role_level || '—'}
                    </p>
                  </div>

                  {onToggleFullView && role && (
                    <div
                      className="flex items-center justify-between gap-3 p-4 rounded-lg bg-cyan-50 border border-cyan-100"
                      data-testid="drawer-full-view-toggle"
                    >
                      <div>
                        <p className="text-sm font-semibold text-cyan-900">Social Media Full View</p>
                        <p className="text-xs text-cyan-700/80 mt-0.5 max-w-xs">
                          {role.social_media_full_view_locked
                            ? `${role.display_name || role.name} already sees ALL posts via its Approve permission — this is always on.`
                            : <>See ALL social media posts org-wide. Affects EVERY user with the
                              "{role.display_name || role.name}" role, not just {user.full_name}.</>}
                        </p>
                      </div>
                      <Switch
                        checked={!!role.social_media_full_view || !!role.social_media_full_view_locked}
                        disabled={fullViewBusy || role.social_media_full_view_locked}
                        onCheckedChange={(checked) => onToggleFullView(role.id, checked)}
                      />
                    </div>
                  )}

                  <Section title="Page Access" Icon={ShieldCheck}>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(permissions.page_access || {}).map(([page, allowed]) => (
                        <div
                          key={page}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${allowed ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                        >
                          {allowed
                            ? <CheckCircle2 size={14} className="text-emerald-500" />
                            : <XCircle size={14} className="text-slate-300" />}
                          <span className="capitalize truncate">{page.replaceAll('_', ' ')}</span>
                        </div>
                      ))}
                      {Object.keys(permissions.page_access || {}).length === 0 && (
                        <p className="text-sm text-slate-500 italic col-span-2">No page-access data.</p>
                      )}
                    </div>
                  </Section>

                  <Section title={`Granular Permissions (${(permissions.permissions || []).length})`} Icon={ShieldCheck}>
                    {(permissions.permissions || []).length === 0
                      ? <p className="text-sm text-slate-500 italic">No granular permissions granted.</p>
                      : (
                        <div className="max-h-64 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
                          {(permissions.permissions || []).map(p => (
                            <div key={p.id} className="flex items-center justify-between px-3 py-2 text-xs">
                              <span className="text-slate-700">
                                <span className="font-semibold text-slate-900">{p.module}</span>
                                {p.action && <span className="text-slate-500"> · {p.action}</span>}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Allowed</span>
                            </div>
                          ))}
                        </div>
                      )}
                  </Section>
                </>
              )}
            </div>
          )}

          {tab === 'activity' && (
            <div className="space-y-3" data-testid="drawer-activity-body">
              {activityLoading && <p className="text-sm text-slate-500">Loading activity…</p>}
              {!activityLoading && activity.length === 0 && (
                <div className="text-center py-8">
                  <Activity size={30} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">No recent activity for this user.</p>
                </div>
              )}
              {!activityLoading && activity.length > 0 && (
                <ul className="relative border-l border-slate-200 space-y-4 pl-4">
                  {activity.map((log) => (
                    <li key={log.id} className="relative">
                      <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-sm" />
                      <p className="text-sm font-medium text-slate-800">
                        {humanAction(log.action)}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{humanTime(log.timestamp)}</p>
                      {log.details && typeof log.details === 'object' && Object.keys(log.details).length > 0 && (
                        <pre className="mt-1 text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1 whitespace-pre-wrap break-all border border-slate-100">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-between">
          <p className="text-[11px] text-slate-400">User ID: <code className="font-mono">{user.id.slice(0, 8)}…</code></p>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="drawer-close-footer">
            Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UserDetailDrawer;
